import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushNotificationSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Register service worker
export async function registerServiceWorker() {
  if (!isPushNotificationSupported()) {
    console.log('Push notifications are not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });
    
    console.log('Service Worker registered successfully:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
}

// Ask for notification permission
export async function askUserPermission() {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return null;
  }

  const permission = await Notification.requestPermission();
  return permission;
}

// Get VAPID public key from backend
export async function getVapidPublicKey() {
  try {
    const response = await axios.get(`${API}/notifications/vapid-public-key`);
    return response.data.publicKey;
  } catch (error) {
    console.error('Error getting VAPID public key:', error);
    throw error;
  }
}

// Subscribe to push notifications
export async function subscribeToPushNotifications(authToken) {
  try {
    console.log('Step 1: Registering service worker...');
    // 1. Register service worker
    const registration = await registerServiceWorker();
    if (!registration) {
      throw new Error('Service Worker registration failed');
    }
    console.log('✅ Service worker registered');

    console.log('Step 2: Requesting notification permission...');
    // 2. Ask for permission
    const permission = await askUserPermission();
    console.log('Permission result:', permission);
    
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }
    console.log('✅ Permission granted');

    console.log('Step 3: Getting VAPID public key...');
    // 3. Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey();
    console.log('VAPID key received:', vapidPublicKey ? vapidPublicKey.substring(0, 20) + '...' : 'null');
    
    if (!vapidPublicKey) {
      throw new Error('Failed to get VAPID public key from server');
    }
    
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    console.log('✅ VAPID key converted');

    console.log('Step 4: Subscribing to push manager...');
    // 4. Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });

    console.log('✅ Push subscription created:', subscription.endpoint.substring(0, 50) + '...');

    console.log('Step 5: Sending subscription to backend...');
    // 5. Send subscription to backend
    const response = await axios.post(
      `${API}/notifications/subscribe`,
      {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      },
      {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }
    );

    console.log('Backend response:', response.data);
    console.log('✅ Successfully subscribed to push notifications');
    return subscription;
  } catch (error) {
    console.error('❌ Error subscribing to push notifications:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPushNotifications(authToken) {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    // Unsubscribe from browser
    await subscription.unsubscribe();

    // Remove subscription from backend
    await axios.delete(
      `${API}/notifications/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }
    );

    console.log('✅ Successfully unsubscribed from push notifications');
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    throw error;
  }
}

// Check if user is subscribed
export async function isPushNotificationSubscribed() {
  if (!isPushNotificationSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return false;
    }

    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
}

// Send test push notification
export async function sendTestPushNotification(authToken) {
  try {
    await axios.post(
      `${API}/notifications/test-push`,
      {},
      {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }
    );
    console.log('✅ Test push notification sent');
  } catch (error) {
    console.error('Error sending test push notification:', error);
    throw error;
  }
}
